// Web session-replay recorder — self-hosted rrweb DOM capture (ED-2570).
//
// Privacy posture (HIPAA): mask-everything defaults. All text is masked
// (maskTextSelector '*'), all inputs are masked, and media/canvas/svg-image
// elements are blocked entirely — the recording holds layout, navigation,
// cursor movement, and interaction, never content. Relaxing masking for a
// specific chrome element is a deliberate future decision, not a default.
//
// Chunking: rrweb emits an event stream; we cut a full snapshot every
// CHECKOUT_MS so the admin player can seek, and upload buffered events every
// FLUSH_MS through the uploader the recorder hands us (which posts to
// /telemetry/replay and enforces the session_replay_enabled flag server-side).
// Upload failures keep up to MAX_PENDING_CHUNKS in memory then drop oldest —
// replay is best-effort; the structured journey stream is the durable record.
//
// Each startReplay() builds a per-session context captured by its own
// closures, so a rotation's in-flight drain can finish uploading the OLD
// session's tail while the new session records — no shared mutable arrays.

import { record } from 'rrweb';
import type { ReplayUploader } from './replay';
export type { ReplayUploader };

const FLUSH_MS = 10_000;
const CHECKOUT_MS = 4 * 60_000;
const MAX_PENDING_CHUNKS = 3;
const MAX_EVENTS_PER_CHUNK = 4000; // server caps at 5000

interface ReplayContext {
  teardown: () => void;
}

let active: ReplayContext | null = null;

// Chunk sequence survives a stop/start WITHIN one session, and only within one.
//
// It used to live in createContext's closure, so every startReplay() began
// again at 0. That was harmless while recording started once per session, and
// destructive the moment it could restart: the S3 key is
// `session-replay/{session_id}/{seq}.json` and step 2 of the ingest is a plain
// put ("same key on retry, pure overwrite"), so a restarted recording would
// OVERWRITE the original opening snapshot — while step 3's
// `ON CONFLICT (session_id, seq) DO NOTHING` silently dropped the new index
// row. The replay would be corrupted in place, with nothing logged anywhere.
//
// Keyed by session id so a rotated session still starts clean at 0.
let seqHighWater: { sessionId: string; next: number } | null = null;

const nextSeqFor = (sessionId: string): number =>
  (seqHighWater && seqHighWater.sessionId === sessionId ? seqHighWater.next : 0);

const rememberSeq = (sessionId: string, next: number): void => {
  seqHighWater = { sessionId, next };
};

// Exported for tests only. Do not call across module boundaries.
export const __resetReplaySeq = (): void => { seqHighWater = null; };

function createContext(uploader: ReplayUploader): ReplayContext | null {
  let buffer: unknown[] = [];
  let pending: Array<{ seq: number; startedAt: string; events: unknown[] }> = [];
  let chunkStartedAt = new Date().toISOString();
  let chunkSeq = nextSeqFor(uploader.sessionId);
  let uploading = false;
  let stopped = false;

  const drain = async (): Promise<void> => {
    if (uploading) return;
    uploading = true;
    try {
      while (pending.length) {
        const head = pending[0];
        let ok = false;
        try {
          ok = await uploader.upload(head.seq, head.startedAt, head.events);
        } catch {
          ok = false;
        }
        if (!ok) return; // leave the queue intact; the next cut retries
        pending.shift();
      }
    } finally {
      uploading = false;
    }
  };

  const cut = (): void => {
    if (!buffer.length) return;
    const seq = chunkSeq++;
    rememberSeq(uploader.sessionId, chunkSeq);
    pending.push({ seq, startedAt: chunkStartedAt, events: buffer });
    buffer = [];
    chunkStartedAt = new Date().toISOString();
    while (pending.length > MAX_PENDING_CHUNKS) pending.shift();
    void drain();
  };

  let stopFn: (() => void) | null = null;
  try {
    stopFn =
      record({
        emit: (event: unknown) => {
          if (stopped) return;
          buffer.push(event);
          if (buffer.length >= MAX_EVENTS_PER_CHUNK) cut();
        },
        maskAllInputs: true,
        maskTextSelector: '*',
        blockSelector: 'img, video, canvas, svg image, iframe',
        inlineStylesheet: true,
        slimDOMOptions: 'all',
        sampling: { mousemove: 60, scroll: 150, media: 800, input: 'last' },
        checkoutEveryNms: CHECKOUT_MS,
        recordCanvas: false,
        collectFonts: false,
      }) ?? null;
  } catch {
    return null; // rrweb refused to start — replay silently off
  }
  if (!stopFn) return null;

  const flushTimer = setInterval(cut, FLUSH_MS);

  return {
    teardown: () => {
      stopped = true;
      clearInterval(flushTimer);
      try {
        stopFn?.();
      } catch {
        // already torn down
      }
      // Final best-effort flush of the tail — the closure keeps draining the
      // old session even after a new startReplay() begins recording.
      cut();
    },
  };
}

export function startReplay(uploader: ReplayUploader): void {
  stopReplay();
  active = createContext(uploader);
}

export function stopReplay(): void {
  active?.teardown();
  active = null;
}
